import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { auth, db } from '../firebase';
import {
  collection,
  getDocs,
  doc,
  writeBatch,
  serverTimestamp,
  query,
  orderBy,
  limit,
  runTransaction,
  addDoc,
  increment,
} from 'firebase/firestore';
import Spinner from './Spinner';
import './AdminDashboard.css';
import './StockManagement.css';
import Inventario from './Inventario';

const quantitiesByProductId = (lines) => {
  const m = {};
  for (const l of lines) {
    const id = l.productId || l.id;
    if (!id) continue;
    const q = Number(l.quantityAdded);
    if (!Number.isFinite(q)) continue;
    const key = stockLineKey(l);
    m[key] = (m[key] || 0) + q;
  }
  return m;
};

const stockDeltasForLineChange = (oldLines, newLines) => {
  const oldM = quantitiesByProductId(oldLines);
  const newM = quantitiesByProductId(newLines);
  const ids = new Set([...Object.keys(oldM), ...Object.keys(newM)]);
  const deltas = {};
  for (const id of ids) {
    const d = (newM[id] || 0) - (oldM[id] || 0);
    if (d !== 0) deltas[id] = d;
  }
  return deltas;
};

const STOCK_CATALOGS = [
  { collectionName: 'products', label: 'Entradas' },
  { collectionName: 'otherProducts', label: 'Consumo interno' },
];

const DEFAULT_STOCK_COLLECTION = 'products';
const STOCK_CATALOG_NAMES = STOCK_CATALOGS.map((catalog) => catalog.collectionName);

const stockCatalogLabel = (collectionName) =>
  STOCK_CATALOGS.find((catalog) => catalog.collectionName === collectionName)?.label || 'Entradas';

const normalizeStockCollectionName = (collectionName) =>
  STOCK_CATALOG_NAMES.includes(collectionName) ? collectionName : DEFAULT_STOCK_COLLECTION;

const stockLineCollection = (line) =>
  normalizeStockCollectionName(line?.productCollection || line?.catalogCollection || line?.collection);

const stockProductKey = (collectionName, productId) =>
  `${normalizeStockCollectionName(collectionName)}::${productId}`;

const stockLineKey = (line) => stockProductKey(stockLineCollection(line), line?.productId || line?.id);

const splitStockProductKey = (key) => {
  const [collectionName, productId] = String(key).split('::');
  return {
    collectionName: normalizeStockCollectionName(collectionName),
    productId,
  };
};

/** Vista colapsada del historial: cantidad de órdenes visibles. */
const HISTORY_PREVIEW_MAX = 4;
/** Tamaño de página al expandir «Ver más». */
const HISTORY_PAGE_SIZE = 8;

const parseHistoryDateYmd = (ymd) => {
  if (!ymd || typeof ymd !== 'string') return null;
  const parts = ymd.split('-');
  if (parts.length !== 3) return null;
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  const d = parseInt(parts[2], 10);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return dt;
};

const startOfLocalDayMs = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0).getTime();

const endOfLocalDayMs = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999).getTime();

const normalizeSupplierName = (name) =>
  String(name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const supplierDocIdFromName = (name) => {
  const normalized = normalizeSupplierName(name);
  return normalized
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 80);
};

const normalizeSearchText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const INITIAL_NEW_PRODUCT_FORM = {
  catalogCollection: DEFAULT_STOCK_COLLECTION,
  model: '',
  price: '',
  costPrice: '',
  quantity: '1',
  category: '',
  newCategory: '',
  description: '',
  addToSelection: true,
  hidden: false,
  excludeFromStock: false,
};

const StockManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [confirmedProducts, setConfirmedProducts] = useState([]);
  const [stockHistory, setStockHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [operationName, setOperationName] = useState('');
  const [expandedHistory, setExpandedHistory] = useState({});
  const [editLoadModal, setEditLoadModal] = useState(null);
  const [editLoadSaving, setEditLoadSaving] = useState(false);
  const [historyViewExpanded, setHistoryViewExpanded] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyFilterDateFrom, setHistoryFilterDateFrom] = useState('');
  const [historyFilterDateTo, setHistoryFilterDateTo] = useState('');
  const [historyFilterName, setHistoryFilterName] = useState('');
  const [newProductModalOpen, setNewProductModalOpen] = useState(false);
  const [newProductSaving, setNewProductSaving] = useState(false);
  const [newProductForm, setNewProductForm] = useState(() => ({ ...INITIAL_NEW_PRODUCT_FORM }));
  const [supplierName, setSupplierName] = useState('');
  const [supplierPaymentMethod, setSupplierPaymentMethod] = useState('');
  const [supplierPaymentStatus, setSupplierPaymentStatus] = useState('Adeudado');
  const [suppliers, setSuppliers] = useState([]);

  const fetchProducts = useCallback(async () => {
    try {
      const snapshots = await Promise.all(
        STOCK_CATALOGS.map(async (catalog) => ({
          ...catalog,
          snap: await getDocs(collection(db, catalog.collectionName)),
        }))
      );
      const productsData = snapshots
        .flatMap(({ collectionName, label, snap }) =>
          snap.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
            catalogCollection: collectionName,
            catalogLabel: label,
            stock: Number(doc.data().stock) || 0,
            costPrice: Number(doc.data().costPrice) || 0,
            excludeFromStock: doc.data().excludeFromStock ?? false
          }))
        )
        .sort((a, b) =>
          `${a.catalogLabel} ${a.model || ''}`.localeCompare(
            `${b.catalogLabel} ${b.model || ''}`,
            'es',
            { sensitivity: 'base' }
          )
        );
      setProducts(productsData);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const fetchStockHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const q = query(
        collection(db, 'stockHistory'),
        orderBy('createdAt', 'desc'),
        limit(150)
      );
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setStockHistory(rows);
    } catch (error) {
      console.error('Error al cargar historial de stock:', error);
      setStockHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStockHistory();
  }, [fetchStockHistory]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'suppliers'));
      const data = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) =>
          String(a.name || '').localeCompare(String(b.name || ''), 'es', { sensitivity: 'base' })
        );
      setSuppliers(data);
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
      setSuppliers([]);
    }
  }, []);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const addProductToStock = (product, quantity) => {
    const parsedQuantity = parseInt(quantity, 10);
    if (parsedQuantity <= 0 || isNaN(parsedQuantity)) {
      alert('Please enter a positive integer quantity');
      return;
    }
    setSelectedProducts(prevSelectedProducts => {
      const key = stockProductKey(product.catalogCollection, product.id);
      const existingProductIndex = prevSelectedProducts.findIndex(item => stockLineKey(item) === key);
      if (existingProductIndex !== -1) {
        const updatedProducts = [...prevSelectedProducts];
        updatedProducts[existingProductIndex] = {
          ...updatedProducts[existingProductIndex],
          quantity: parsedQuantity
        };
        return updatedProducts;
      } else {
        return [...prevSelectedProducts, { ...product, quantity: parsedQuantity, costPrice: product.costPrice ?? '' }];
      }
    });
  };

  const updateSelectedCostPrice = (productKey, value) => {
    setSelectedProducts(prev => prev.map(item =>
      stockLineKey(item) === productKey ? { ...item, costPrice: value } : item
    ));
  };

  const removeProductFromStock = (productKey) => {
    setSelectedProducts(prevSelectedProducts => prevSelectedProducts.filter(item => stockLineKey(item) !== productKey));
  };

  const handleStockUpdate = async () => {
    if (isSubmitting) return;
    const nameTrimCreate = (operationName || '').trim();
    const supplierTrimCreate = (supplierName || '').trim();
    const supplierPaymentMethodTrim = (supplierPaymentMethod || '').trim();
    const supplierPaymentStatusTrim = (supplierPaymentStatus || '').trim();
    if (!nameTrimCreate) {
      alert('El nombre de la carga es obligatorio.');
      return;
    }
    if (!supplierTrimCreate) {
      alert('El nombre del proveedor es obligatorio.');
      return;
    }
    if (!supplierPaymentStatusTrim) {
      alert('El estado de pago al proveedor es obligatorio.');
      return;
    }
    const supplierDocId = supplierDocIdFromName(supplierTrimCreate);
    if (!supplierDocId) {
      alert('Ingresá un nombre de proveedor válido.');
      return;
    }
    setIsSubmitting(true);

    try {
      const batch = writeBatch(db);
      const user = auth.currentUser;
      const lines = [];
      const sheetRef = doc(collection(db, 'stockHistory'));
      const supplierRef = doc(db, 'suppliers', supplierDocId);
      let supplierUnits = 0;
      let supplierAmount = 0;

      selectedProducts.forEach((product) => {
        const productCollection = normalizeStockCollectionName(product.catalogCollection);
        const productRef = doc(db, productCollection, product.id);
        const newStock = product.stock + product.quantity;
        const updateData = { stock: newStock };
        const previousCostPrice = Number(product.costPrice) || 0;
        const costPriceNum = product.costPrice !== '' && product.costPrice != null && !isNaN(Number(product.costPrice))
          ? parseFloat(product.costPrice)
          : null;
        if (costPriceNum !== null && costPriceNum >= 0) {
          updateData.costPrice = costPriceNum;
        }
        const costPriceAfter =
          costPriceNum !== null && costPriceNum >= 0 ? costPriceNum : previousCostPrice;
        const costPriceUpdated =
          costPriceNum !== null &&
          costPriceNum >= 0 &&
          Math.abs(costPriceNum - previousCostPrice) > 1e-9;

        batch.update(productRef, updateData);

        lines.push({
          productCollection,
          productId: product.id,
          productModel: product.model ?? '',
          quantityAdded: product.quantity,
          stockBefore: product.stock,
          stockAfter: newStock,
          previousCostPrice,
          costPriceAfter,
          costPriceUpdated,
        });
        supplierUnits += Number(product.quantity) || 0;
        if (costPriceAfter != null && Number.isFinite(costPriceAfter)) {
          supplierAmount += (Number(product.quantity) || 0) * costPriceAfter;
        }
      });

      batch.set(sheetRef, {
        schemaVersion: 2,
        createdAt: serverTimestamp(),
        userId: user?.uid ?? null,
        userEmail: user?.email ?? null,
        operationName: nameTrimCreate.slice(0, 120),
        supplierName: supplierTrimCreate.slice(0, 120),
        supplierPaymentMethod: supplierPaymentMethodTrim.slice(0, 40),
        supplierPaymentStatus: supplierPaymentStatusTrim.slice(0, 40),
        lines,
      });

      batch.set(
        supplierRef,
        {
          name: supplierTrimCreate.slice(0, 120),
          normalizedName: normalizeSupplierName(supplierTrimCreate).slice(0, 120),
          totalPurchasedAmount: increment(supplierAmount),
          totalPurchasedUnits: increment(supplierUnits),
          purchaseCount: increment(1),
          lastPaymentMethod: supplierPaymentMethodTrim ? supplierPaymentMethodTrim.slice(0, 40) : null,
          lastPaymentStatus: supplierPaymentStatusTrim.slice(0, 40),
          lastPurchaseAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await batch.commit();
      alert('Stock actualizado correctamente.');
      setConfirmedProducts(selectedProducts);
      setSelectedProducts([]);
      setOperationName('');
      setSupplierName('');
      setSupplierPaymentMethod('');
      setSupplierPaymentStatus('Adeudado');
      setSearchTerm('');
      fetchStockHistory();
      fetchSuppliers();
    } catch (error) {
      console.error("Error updating stock:", error);
      alert('Error al actualizar el stock. Intentá de nuevo.');
    } finally {
      setIsSubmitting(false);
      fetchProducts();
    }
  };

  const productCategories = useMemo(() => {
    const fromCat = products.map((p) => p.category).filter(Boolean);
    const fromCats = products.flatMap((p) =>
      Array.isArray(p.categories) ? p.categories : []
    );
    return [...new Set([...fromCat, ...fromCats])].sort((a, b) => a.localeCompare(b));
  }, [products]);

  const handleCreateNewProduct = async () => {
    if (newProductSaving) return;
    const model = newProductForm.model.trim();
    if (!model) {
      alert('El nombre del producto es obligatorio.');
      return;
    }
    const price = parseFloat(newProductForm.price);
    if (!Number.isFinite(price) || price < 0) {
      alert('Ingresá un precio de venta válido (mayor o igual a 0).');
      return;
    }
    const costPrice = parseFloat(newProductForm.costPrice);
    if (!Number.isFinite(costPrice) || costPrice < 0) {
      alert('Ingresá un precio de costo válido (mayor o igual a 0).');
      return;
    }
    let cat = newProductForm.category;
    if (cat === '__custom__') {
      cat = newProductForm.newCategory.trim();
      if (!cat) {
        alert('Ingresá el nombre de la nueva categoría o elegí otra opción en el listado.');
        return;
      }
    }
    const qty = parseInt(newProductForm.quantity, 10);
    if (newProductForm.addToSelection) {
      if (!Number.isFinite(qty) || qty < 1) {
        alert('Ingresá una cantidad entera mayor a 0 para sumar a la carga actual.');
        return;
      }
    }

    setNewProductSaving(true);
    try {
      const catalogCollection = normalizeStockCollectionName(newProductForm.catalogCollection);
      const productData = {
        model,
        price,
        costPrice,
        stock: 0,
        category: cat || '',
        categories: cat ? [cat] : [],
        description: (newProductForm.description || '').trim(),
        images: [],
        hidden: Boolean(newProductForm.hidden),
        discountExcluded: false,
        excludeFromStock: Boolean(newProductForm.excludeFromStock),
      };
      const ref = await addDoc(collection(db, catalogCollection), productData);
      if (newProductForm.addToSelection) {
        const row = {
          ...productData,
          id: ref.id,
          catalogCollection,
          catalogLabel: stockCatalogLabel(catalogCollection),
          quantity: qty,
          costPrice,
        };
        setSelectedProducts((prev) => {
          const i = prev.findIndex((x) => x.id === ref.id);
          if (i >= 0) {
            const next = [...prev];
            next[i] = row;
            return next;
          }
          return [...prev, row];
        });
      }
      await fetchProducts();
      setNewProductModalOpen(false);
      setNewProductForm({ ...INITIAL_NEW_PRODUCT_FORM });
      alert(
        newProductForm.addToSelection
          ? 'Producto creado y añadido a la selección. Completá el nombre de la carga y confirmá con «Agregar stock».'
          : 'Producto creado. Podés buscarlo arriba para ingresar stock cuando quieras.'
      );
    } catch (error) {
      console.error('Error al crear producto:', error);
      alert('No se pudo crear el producto. Intentá de nuevo.');
    } finally {
      setNewProductSaving(false);
    }
  };

  // Solo mostrar productos que computan stock (no excluidos) para agregar inventario
  const normalizedSearchTerm = normalizeSearchText(searchTerm);

  const filteredProducts = normalizedSearchTerm
    ? products
        .filter((product) => {
          const searchableText = normalizeSearchText(
            `${product.model || ''} ${product.category || ''} ${product.id || ''} ${product.catalogLabel || ''}`
          );
          return searchableText.includes(normalizedSearchTerm);
        })
        .sort((a, b) => b.stock - a.stock)
    : [];

  const formatHistoryDate = (ts) => {
    if (!ts) return '—';
    try {
      const d = typeof ts.toDate === 'function' ? ts.toDate() : ts;
      if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '—';
      return d.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return '—';
    }
  };

  const formatHistoryMoney = (n) => {
    if (typeof n !== 'number' || Number.isNaN(n)) return '—';
    return `$${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  /** Precio de costo unitario vigente al momento del registro (siempre se guarda en Firestore). */
  const historyRowUnitCost = (row) => {
    const after = Number(row.costPriceAfter);
    if (Number.isFinite(after)) return after;
    const prev = Number(row.previousCostPrice);
    if (Number.isFinite(prev)) return prev;
    return null;
  };

  const computeSheetTotals = (rows) => {
    let totalUnits = 0;
    let totalAmount = 0;
    for (const row of rows) {
      const q = Number(row.quantityAdded);
      if (Number.isFinite(q)) totalUnits += q;
      const unit = historyRowUnitCost(row);
      if (Number.isFinite(q) && unit != null && Number.isFinite(unit)) {
        totalAmount += q * unit;
      }
    }
    return { lineCount: rows.length, totalUnits, totalAmount };
  };

  const toggleHistoryDetail = useCallback((key) => {
    setExpandedHistory((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const openEditLoad = useCallback(
    (sheet) => {
      if (!sheet.editable || !sheet.firestoreDocId) return;
      const raw = stockHistory.find((x) => x.id === sheet.firestoreDocId);
      if (!raw?.lines?.length) return;
      const clone = JSON.parse(JSON.stringify(raw.lines));
      setEditLoadModal({
        docId: sheet.firestoreDocId,
        operationName: typeof raw.operationName === 'string' ? raw.operationName : '',
        lines: clone,
        baselineLines: JSON.parse(JSON.stringify(raw.lines)),
      });
    },
    [stockHistory]
  );

  useEffect(() => {
    if (!editLoadModal) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !editLoadSaving) setEditLoadModal(null);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [editLoadModal, editLoadSaving]);

  useEffect(() => {
    if (!newProductModalOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !newProductSaving) {
        setNewProductModalOpen(false);
        setNewProductForm({ ...INITIAL_NEW_PRODUCT_FORM });
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [newProductModalOpen, newProductSaving]);

  const updateEditLoadLine = (index, field, value) => {
    setEditLoadModal((m) => {
      if (!m) return m;
      const lines = m.lines.map((row, i) => (i === index ? { ...row, [field]: value } : row));
      return { ...m, lines };
    });
  };

  const removeEditLoadLine = (index) => {
    setEditLoadModal((m) => {
      if (!m) return m;
      if (m.lines.length <= 1) {
        alert('Para quitar el único producto, usá «Eliminar operación completa».');
        return m;
      }
      return { ...m, lines: m.lines.filter((_, i) => i !== index) };
    });
  };

  const saveEditLoad = async () => {
    if (!editLoadModal || editLoadSaving) return;
    const m = editLoadModal;
    if (m.lines.length < 1) {
      alert('Tiene que haber al menos una línea, o eliminá la operación completa.');
      return;
    }

    const normalizedLines = [];
    for (const line of m.lines) {
      const q = parseInt(line.quantityAdded, 10);
      if (q < 1 || Number.isNaN(q)) {
        alert('Cada cantidad debe ser un entero mayor o igual a 1.');
        return;
      }
      const stockBefore = Number(line.stockBefore);
      const sb = Number.isFinite(stockBefore) ? stockBefore : 0;
      normalizedLines.push({
        productCollection: stockLineCollection(line),
        productId: line.productId,
        productModel: line.productModel ?? '',
        quantityAdded: q,
        stockBefore: sb,
        stockAfter: sb + q,
        previousCostPrice: Number(line.previousCostPrice) || 0,
        costPriceAfter: Number(line.costPriceAfter) || 0,
        costPriceUpdated: !!line.costPriceUpdated,
      });
    }

    const user = auth.currentUser;
    setEditLoadSaving(true);
    try {
      await runTransaction(db, async (tx) => {
        const histRef = doc(db, 'stockHistory', m.docId);
        const hSnap = await tx.get(histRef);
        if (!hSnap.exists()) throw new Error('MISSING');

        const deltas = stockDeltasForLineChange(m.baselineLines, normalizedLines);
        const productKeys = Object.keys(deltas);

        const productReads = [];
        for (const key of productKeys) {
          const { collectionName, productId } = splitStockProductKey(key);
          const pref = doc(db, collectionName, productId);
          const ps = await tx.get(pref);
          productReads.push({ pref, ps, key, productId });
        }
        for (const { pref, ps, key } of productReads) {
          if (!ps.exists()) throw new Error('NO_PRODUCT');
          const stock = Number(ps.data().stock) || 0;
          const newStock = stock + deltas[key];
          if (newStock < 0) throw new Error('NEGATIVE_STOCK');
          tx.update(pref, { stock: newStock });
        }

        const nameTrim = (m.operationName || '').trim();
        tx.update(histRef, {
          operationName: nameTrim ? nameTrim.slice(0, 120) : null,
          lines: normalizedLines,
          lastEditedAt: serverTimestamp(),
          lastEditedByUid: user?.uid ?? null,
          lastEditedByEmail: user?.email ?? null,
        });
      });

      setEditLoadModal(null);
      fetchStockHistory();
      fetchProducts();
      alert('Carga actualizada.');
    } catch (err) {
      console.error(err);
      if (err?.message === 'NEGATIVE_STOCK') {
        alert(
          'No se puede aplicar el cambio: el stock actual no alcanza (¿hubo ventas desde esta carga?). Revisá cantidades o el inventario.'
        );
      } else if (err?.message === 'NO_PRODUCT') {
        alert('Un producto de esta carga ya no existe en el catálogo.');
      } else {
        alert('No se pudo guardar. Intentá de nuevo.');
      }
    } finally {
      setEditLoadSaving(false);
    }
  };

  const deleteWholeLoad = async () => {
    if (!editLoadModal || editLoadSaving) return;
    if (
      !window.confirm(
        '¿Eliminar toda esta operación? Se restará del stock actual lo que se había sumado en cada línea y se borrará el registro.'
      )
    ) {
      return;
    }

    const docId = editLoadModal.docId;
    setEditLoadSaving(true);
    try {
      await runTransaction(db, async (tx) => {
        const histRef = doc(db, 'stockHistory', docId);
        const hSnap = await tx.get(histRef);
        if (!hSnap.exists()) throw new Error('MISSING');
        const lines = hSnap.data().lines || [];
        const byId = quantitiesByProductId(lines);
        const productKeys = Object.keys(byId);
        const productReads = [];
        for (const key of productKeys) {
          const { collectionName, productId } = splitStockProductKey(key);
          const pref = doc(db, collectionName, productId);
          const ps = await tx.get(pref);
          productReads.push({ pref, ps, key, q: byId[key] });
        }
        for (const { pref, ps, q } of productReads) {
          if (!ps.exists()) throw new Error('NO_PRODUCT');
          const stock = Number(ps.data().stock) || 0;
          const newStock = stock - q;
          if (newStock < 0) throw new Error('NEGATIVE_STOCK');
          tx.update(pref, { stock: newStock });
        }
        tx.delete(histRef);
      });

      setEditLoadModal(null);
      fetchStockHistory();
      fetchProducts();
      alert('Operación eliminada.');
    } catch (err) {
      console.error(err);
      if (err?.message === 'NEGATIVE_STOCK') {
        alert(
          'No se puede revertir por completo: el stock actual es menor que lo ingresado en esta carga. Ajustá manualmente el inventario si hace falta.'
        );
      } else {
        alert('No se pudo eliminar. Intentá de nuevo.');
      }
    } finally {
      setEditLoadSaving(false);
    }
  };

  const historySheets = useMemo(() => {
    const timeMs = (ts) => {
      if (!ts) return 0;
      try {
        const d = typeof ts.toDate === 'function' ? ts.toDate() : ts;
        if (!(d instanceof Date) || Number.isNaN(d.getTime())) return 0;
        return d.getTime();
      } catch {
        return 0;
      }
    };

    const sheets = [];

    for (const doc of stockHistory) {
      if (Array.isArray(doc.lines) && doc.lines.length > 0) {
        const opName = doc.operationName;
        const opStr =
          typeof opName === 'string' && opName.trim() !== '' ? opName.trim() : null;
        sheets.push({
          key: doc.id,
          firestoreDocId: doc.id,
          editable: true,
          createdAt: doc.createdAt,
          userId: doc.userId ?? null,
          userEmail: doc.userEmail ?? null,
          operationName: opStr,
          supplierName: typeof doc.supplierName === 'string' ? doc.supplierName.trim() : null,
          supplierPaymentMethod:
            typeof doc.supplierPaymentMethod === 'string' ? doc.supplierPaymentMethod.trim() : null,
          supplierPaymentStatus:
            typeof doc.supplierPaymentStatus === 'string' ? doc.supplierPaymentStatus.trim() : null,
          rows: doc.lines.map((line, idx) => ({
            ...line,
            id: `${doc.id}-L${idx}`,
          })),
        });
      }
    }

    const legacyMap = new Map();
    for (const row of stockHistory) {
      if (Array.isArray(row.lines) && row.lines.length > 0) continue;
      if (row.productId == null && row.quantityAdded == null) continue;

      const gKey = row.groupId ? String(row.groupId) : `legacy:${row.id}`;
      if (!legacyMap.has(gKey)) {
        legacyMap.set(gKey, {
          key: gKey,
          firestoreDocId: null,
          editable: false,
          createdAt: row.createdAt,
          userId: row.userId ?? null,
          userEmail: row.userEmail ?? null,
          operationName: null,
          supplierName: typeof row.supplierName === 'string' ? row.supplierName.trim() : null,
          supplierPaymentMethod:
            typeof row.supplierPaymentMethod === 'string' ? row.supplierPaymentMethod.trim() : null,
          supplierPaymentStatus:
            typeof row.supplierPaymentStatus === 'string' ? row.supplierPaymentStatus.trim() : null,
          rows: [],
        });
      }
      const sheet = legacyMap.get(gKey);
      sheet.rows.push({ ...row, id: row.id });
    }

    sheets.push(...legacyMap.values());

    sheets.forEach((sheet) => {
      sheet.rows.sort((a, b) =>
        String(a.productModel || '').localeCompare(String(b.productModel || ''), 'es', {
          sensitivity: 'base',
        })
      );
    });

    sheets.sort((a, b) => timeMs(b.createdAt) - timeMs(a.createdAt));
    return sheets;
  }, [stockHistory]);

  const filteredHistorySheets = useMemo(() => {
    let fromD = parseHistoryDateYmd(historyFilterDateFrom);
    let toD = parseHistoryDateYmd(historyFilterDateTo);
    let fromMs = fromD != null ? startOfLocalDayMs(fromD) : null;
    let toMs = toD != null ? endOfLocalDayMs(toD) : null;
    if (fromMs != null && toMs != null && fromMs > toMs) {
      const tmpFrom = fromD;
      fromD = toD;
      toD = tmpFrom;
      fromMs = fromD != null ? startOfLocalDayMs(fromD) : null;
      toMs = toD != null ? endOfLocalDayMs(toD) : null;
    }

    const nameQ = historyFilterName.trim().toLowerCase();

    return historySheets.filter((sheet) => {
      let sheetMs = null;
      try {
        const t = sheet.createdAt;
        const dt = typeof t?.toDate === 'function' ? t.toDate() : t;
        if (dt instanceof Date && !Number.isNaN(dt.getTime())) sheetMs = dt.getTime();
      } catch {
        sheetMs = null;
      }

      if ((fromMs != null || toMs != null) && sheetMs == null) return false;
      if (fromMs != null && sheetMs < fromMs) return false;
      if (toMs != null && sheetMs > toMs) return false;

      if (nameQ) {
        const op = String(sheet.operationName || '').toLowerCase();
        if (op.includes(nameQ)) return true;
        const supplier = String(sheet.supplierName || '').toLowerCase();
        if (supplier.includes(nameQ)) return true;
        const lineHit = sheet.rows.some((row) =>
          String(row.productModel || '').toLowerCase().includes(nameQ)
        );
        if (!lineHit) return false;
      }

      return true;
    });
  }, [historySheets, historyFilterDateFrom, historyFilterDateTo, historyFilterName]);

  const historyPageCount = useMemo(
    () => Math.max(1, Math.ceil(filteredHistorySheets.length / HISTORY_PAGE_SIZE)),
    [filteredHistorySheets.length]
  );

  const displayedHistorySheets = useMemo(() => {
    if (!historyViewExpanded) {
      return filteredHistorySheets.slice(0, HISTORY_PREVIEW_MAX);
    }
    const start = historyPage * HISTORY_PAGE_SIZE;
    return filteredHistorySheets.slice(start, start + HISTORY_PAGE_SIZE);
  }, [filteredHistorySheets, historyViewExpanded, historyPage]);

  useEffect(() => {
    setHistoryPage(0);
  }, [historyFilterDateFrom, historyFilterDateTo, historyFilterName]);

  useEffect(() => {
    const pages = Math.max(1, Math.ceil(filteredHistorySheets.length / HISTORY_PAGE_SIZE));
    setHistoryPage((p) => (p >= pages ? pages - 1 : p));
  }, [filteredHistorySheets.length]);

  if (loading) return <Spinner />;

  return (
    <div className="stock-management">
      <div className="stock-management__inner">
        <header className="stock-management-header">
          <h1 className="stock-management-title">Gestión de Inventario</h1>
        </header>

        <section className="stock-management-add" aria-label="Agregar stock">
          <div className="stock-management-add__card">
            <div className="stock-management-add-card-header">
              <h2 className="stock-management-section-title stock-management-add-card-title">Agregar stock</h2>
              <button
                type="button"
                className="stock-management-new-product-btn"
                onClick={() => setNewProductModalOpen(true)}
              >
                Agregar nuevo producto
              </button>
            </div>
            <p className="stock-management-section-desc">Busca el producto e ingresa la cantidad a sumar al inventario.</p>
            <div className="stock-management-search-wrap">
              <input
                type="search"
                placeholder="Buscar por nombre de producto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="stock-management-search-input"
                aria-label="Buscar producto"
              />
            </div>
            {searchTerm && (
              <div className="stock-management-products-list">
                {filteredProducts.length === 0 ? (
                  <p className="stock-management-empty">No hay productos que coincidan con la búsqueda.</p>
                ) : (
                  filteredProducts.map(product => (
                    <article key={stockProductKey(product.catalogCollection, product.id)} className="stock-management-product-item">
                      <img src={product.images?.[0] ?? ''} alt="" className="stock-management-product-image" />
                      <div className="stock-management-product-info">
                        <span className="stock-management-product-name">{product.model}</span>
                        <span className="stock-management-product-meta">
                          {product.catalogLabel} · ${Number(product.price).toLocaleString('es-AR')} · Stock actual: <strong>{product.stock}</strong>
                          {product.excludeFromStock === true ? ' · No computa stock' : ''}
                        </span>
                        <div className="stock-management-product-actions">
                          <label className="stock-management-quantity-label">
                            <span className="sr-only">Cantidad a agregar para {product.model}</span>
                            <input
                              type="number"
                              placeholder="Cant."
                              min="1"
                              onChange={(e) => addProductToStock(product, e.target.value)}
                              className="stock-management-quantity-input"
                            />
                          </label>
                        </div>
                      </div>
                    </article>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="stock-management-selected-card">
            <h3 className="stock-management-selected-title">Productos seleccionados</h3>
            {selectedProducts.length === 0 ? (
              <p className="stock-management-selected-empty">Ningún producto seleccionado. Busca arriba e ingresa cantidades.</p>
            ) : (
              <>
                <label className="stock-management-operation-name-label">
                  <span className="stock-management-operation-name-text">
                    Nombre de la carga <abbr title="obligatorio" className="stock-management-required-mark">*</abbr>
                  </span>
                  <input
                    type="text"
                    value={operationName}
                    onChange={(e) => setOperationName(e.target.value)}
                    placeholder="Ej.: Reposición proveedor · Abril"
                    maxLength={120}
                    className="stock-management-operation-name-input"
                    required
                    aria-required="true"
                    aria-label="Nombre de la operación de carga de stock (obligatorio)"
                  />
                </label>
                <label className="stock-management-operation-name-label">
                  <span className="stock-management-operation-name-text">
                    Nombre de proveedor <abbr title="obligatorio" className="stock-management-required-mark">*</abbr>
                  </span>
                  <input
                    type="text"
                    list="stock-management-suppliers-list"
                    value={supplierName}
                    onChange={(e) => setSupplierName(e.target.value)}
                    placeholder="Escribí o elegí un proveedor"
                    maxLength={120}
                    className="stock-management-operation-name-input"
                    required
                    aria-required="true"
                    aria-label="Nombre del proveedor (obligatorio)"
                  />
                  <datalist id="stock-management-suppliers-list">
                    {suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.name || ''} />
                    ))}
                  </datalist>
                </label>
                <label className="stock-management-operation-name-label">
                  <span className="stock-management-operation-name-text">
                    Forma de pago al proveedor
                  </span>
                  <select
                    value={supplierPaymentMethod}
                    onChange={(e) => setSupplierPaymentMethod(e.target.value)}
                    className="stock-management-operation-name-input"
                    aria-label="Forma de pago al proveedor (opcional)"
                  >
                    <option value="">Sin especificar</option>
                    <option value="Efectivo">Efectivo</option>
                    <option value="Transferencia">Transferencia</option>
                    <option value="Cheque">Cheque</option>
                  </select>
                </label>
                <label className="stock-management-operation-name-label">
                  <span className="stock-management-operation-name-text">
                    Estado de pago <abbr title="obligatorio" className="stock-management-required-mark">*</abbr>
                  </span>
                  <select
                    value={supplierPaymentStatus}
                    onChange={(e) => setSupplierPaymentStatus(e.target.value)}
                    className="stock-management-operation-name-input"
                    required
                    aria-required="true"
                    aria-label="Estado de pago al proveedor (obligatorio)"
                  >
                    <option value="Adeudado">Adeudado</option>
                    <option value="Pagado">Pagado</option>
                  </select>
                </label>
                <ul className="stock-management-selected-list">
                  {selectedProducts.map(product => {
                    const productKey = stockLineKey(product);
                    return (
                    <li key={productKey} className="stock-management-selected-item">
                      <div className="stock-management-selected-item-main">
                        <span className="stock-management-selected-item-text">{product.model} · {stockCatalogLabel(product.catalogCollection)} · <strong>{product.quantity}</strong> unidades</span>
                        <label className="stock-management-cost-price-label">
                          <span className="stock-management-cost-price-label-text">Precio de costo</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Opcional"
                            value={product.costPrice === 0 ? '' : (product.costPrice ?? '')}
                            onChange={(e) => updateSelectedCostPrice(productKey, e.target.value)}
                            className="stock-management-cost-price-input"
                            aria-label={`Precio de costo para ${product.model}`}
                          />
                        </label>
                      </div>
                      <button type="button" onClick={() => removeProductFromStock(productKey)} className="stock-management-remove-btn" aria-label={`Quitar ${product.model}`}>Quitar</button>
                    </li>
                    );
                  })}
                </ul>
                <div className="stock-management-submit-wrap">
                  <button
                    type="button"
                    onClick={handleStockUpdate}
                    className={`stock-management-submit-btn ${isSubmitting ? 'stock-management-submit-btn-disabled' : ''}`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Guardando…' : 'Agregar stock'}
                  </button>
                  {isSubmitting && <Spinner />}
                </div>
              </>
            )}
          </div>

          {confirmedProducts.length > 0 && (
            <div className="stock-management-summary-card">
              <h3 className="stock-management-summary-title">Último movimiento</h3>
              <ul className="stock-management-summary-list">
                {confirmedProducts.map(product => (
                  <li key={stockLineKey(product)} className="stock-management-summary-item">
                    <img src={product.images?.[0] ?? ''} alt="" className="stock-management-summary-image" />
                    <span>{product.model} · {stockCatalogLabel(product.catalogCollection)} · +{product.quantity} unidades</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        <section className="stock-management-history" aria-label="Historial de ingresos de stock">
          <div className="stock-management-history-card">
            <h2 className="stock-management-section-title">Historial de ingresos</h2>
            <p className="stock-management-section-desc">
              Filtrá por fechas y nombre (operación o producto). Se muestran {HISTORY_PREVIEW_MAX} resultados; «Ver más» pagina de a {HISTORY_PAGE_SIZE}. Las cargas con documento único se pueden editar o eliminar.
            </p>
            {historyLoading ? (
              <Spinner />
            ) : stockHistory.length === 0 ? (
              <p className="stock-management-history-empty">Todavía no hay movimientos registrados.</p>
            ) : (
              <>
              <div className="stock-management-history-filters" role="search" aria-label="Filtros del historial">
                <div className="stock-management-history-filters-row">
                  <label className="stock-management-history-filter-field">
                    <span className="stock-management-history-filter-label">Desde</span>
                    <input
                      type="date"
                      value={historyFilterDateFrom}
                      onChange={(e) => setHistoryFilterDateFrom(e.target.value)}
                      className="stock-management-history-filter-date"
                    />
                  </label>
                  <label className="stock-management-history-filter-field">
                    <span className="stock-management-history-filter-label">Hasta</span>
                    <input
                      type="date"
                      value={historyFilterDateTo}
                      onChange={(e) => setHistoryFilterDateTo(e.target.value)}
                      className="stock-management-history-filter-date"
                    />
                  </label>
                  <label className="stock-management-history-filter-field stock-management-history-filter-grow">
                    <span className="stock-management-history-filter-label">Buscar por nombre</span>
                    <input
                      type="search"
                      value={historyFilterName}
                      onChange={(e) => setHistoryFilterName(e.target.value)}
                      placeholder="Nombre de la carga o producto…"
                      className="stock-management-history-filter-search"
                      aria-label="Buscar en historial por nombre de carga o producto"
                    />
                  </label>
                  <button
                    type="button"
                    className="stock-management-history-filter-clear"
                    onClick={() => {
                      setHistoryFilterDateFrom('');
                      setHistoryFilterDateTo('');
                      setHistoryFilterName('');
                    }}
                  >
                    Limpiar filtros
                  </button>
                </div>
              </div>

              {filteredHistorySheets.length === 0 ? (
                <p className="stock-management-history-empty">
                  No hay cargas que coincidan con los filtros.
                </p>
              ) : (
              <>
              <div className="stock-management-history-sheets">
                {displayedHistorySheets.map((sheet) => {
                  const when = formatHistoryDate(sheet.createdAt);
                  const who = sheet.userEmail || sheet.userId || '—';
                  const totals = computeSheetTotals(sheet.rows);
                  const expanded = !!expandedHistory[sheet.key];
                  const detailId = `hist-detail-${String(sheet.key).replace(/[^a-zA-Z0-9_-]/g, '_')}`;
                  const displayName = sheet.operationName || 'Sin nombre';
                  return (
                    <article key={sheet.key} className="stock-management-history-sheet">
                      <div className="stock-management-history-sheet-row">
                        <div className="stock-management-history-sheet-line" aria-label="Resumen del ingreso">
                          <span className="stock-management-history-sheet-date-inline">{when}</span>
                          <span className="stock-management-history-sheet-sep" aria-hidden="true">
                            ·
                          </span>
                          <span className="stock-management-history-sheet-title-inline" title={displayName}>
                            {displayName}
                          </span>
                          {sheet.supplierName ? (
                            <>
                              <span className="stock-management-history-sheet-sep" aria-hidden="true">
                                ·
                              </span>
                              <span
                                className="stock-management-history-sheet-supplier-inline"
                                title={sheet.supplierName}
                              >
                                Prov: {sheet.supplierName}
                              </span>
                            </>
                          ) : null}
                          <span className="stock-management-history-sheet-sep" aria-hidden="true">
                            ·
                          </span>
                          <span className="stock-management-history-sheet-stats">
                            {totals.lineCount} ítem{totals.lineCount !== 1 ? 's' : ''} ·{' '}
                            {totals.totalUnits} u. · Total{' '}
                            {formatHistoryMoney(totals.totalAmount)}
                          </span>
                          <span className="stock-management-history-sheet-sep" aria-hidden="true">
                            ·
                          </span>
                          <span className="stock-management-history-sheet-user-inline">{who}</span>
                          {sheet.supplierPaymentMethod ? (
                            <>
                              <span className="stock-management-history-sheet-sep" aria-hidden="true">
                                ·
                              </span>
                              <span className="stock-management-history-sheet-supplier-inline">
                                Pago: {sheet.supplierPaymentMethod}
                              </span>
                            </>
                          ) : null}
                          {sheet.supplierPaymentStatus ? (
                            <>
                              <span className="stock-management-history-sheet-sep" aria-hidden="true">
                                ·
                              </span>
                              <span className="stock-management-history-sheet-supplier-inline">
                                Estado: {sheet.supplierPaymentStatus}
                              </span>
                            </>
                          ) : null}
                        </div>
                        <div className="stock-management-history-sheet-actions">
                          <button
                            type="button"
                            className="stock-management-history-toggle-btn"
                            onClick={() => toggleHistoryDetail(sheet.key)}
                            aria-expanded={expanded}
                            aria-controls={detailId}
                          >
                            {expanded ? 'Ver menos' : 'Ver más'}
                          </button>
                          {sheet.editable ? (
                            <button
                              type="button"
                              className="stock-management-history-edit-btn"
                              onClick={() => openEditLoad(sheet)}
                            >
                              Editar
                            </button>
                          ) : null}
                        </div>
                      </div>
                      {expanded ? (
                        <div
                          id={detailId}
                          className="stock-management-history-sheet-detail"
                          role="region"
                          aria-label={`Detalle de ${displayName}`}
                        >
                          <div className="stock-management-history-sheet-table-wrap">
                            <table className="stock-management-history-table">
                              <thead>
                                <tr>
                                  <th>Catálogo</th>
                                  <th>Producto</th>
                                  <th>+Unidades</th>
                                  <th>Stock (antes → después)</th>
                                  <th>Precio costo (u.)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sheet.rows.map((row) => (
                                  <tr key={row.id}>
                                    <td>{stockCatalogLabel(stockLineCollection(row))}</td>
                                    <td>{row.productModel || '—'}</td>
                                    <td>{row.quantityAdded ?? '—'}</td>
                                    <td>
                                      {row.stockBefore != null && row.stockAfter != null
                                        ? `${row.stockBefore} → ${row.stockAfter}`
                                        : '—'}
                                    </td>
                                    <td
                                      title={
                                        row.costPriceUpdated
                                          ? 'Se actualizó el precio de costo en este ingreso'
                                          : undefined
                                      }
                                    >
                                      {(() => {
                                        const unit = historyRowUnitCost(row);
                                        return unit == null ? '—' : formatHistoryMoney(unit);
                                      })()}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>

              {!historyViewExpanded && filteredHistorySheets.length > HISTORY_PREVIEW_MAX ? (
                <div className="stock-management-history-list-actions">
                  <button
                    type="button"
                    className="stock-management-history-expand-btn"
                    onClick={() => {
                      setHistoryViewExpanded(true);
                      setHistoryPage(0);
                    }}
                  >
                    Ver más ({filteredHistorySheets.length} cargas con este filtro)
                  </button>
                </div>
              ) : null}

              {historyViewExpanded ? (
                <>
                  {historyPageCount > 1 ? (
                    <nav
                      className="stock-management-history-pagination"
                      aria-label="Paginación del historial"
                    >
                      <button
                        type="button"
                        className="stock-management-history-page-btn"
                        disabled={historyPage <= 0}
                        onClick={() => setHistoryPage((p) => Math.max(0, p - 1))}
                      >
                        Anterior
                      </button>
                      <span className="stock-management-history-page-info">
                        Página {historyPage + 1} de {historyPageCount}
                      </span>
                      <button
                        type="button"
                        className="stock-management-history-page-btn"
                        disabled={historyPage >= historyPageCount - 1}
                        onClick={() =>
                          setHistoryPage((p) => Math.min(historyPageCount - 1, p + 1))
                        }
                      >
                        Siguiente
                      </button>
                    </nav>
                  ) : null}
                  <div className="stock-management-history-list-actions">
                    <button
                      type="button"
                      className="stock-management-history-collapse-btn"
                      onClick={() => {
                        setHistoryViewExpanded(false);
                        setHistoryPage(0);
                      }}
                    >
                      Ver solo las últimas {HISTORY_PREVIEW_MAX}
                    </button>
                  </div>
                </>
              ) : null}
              </>
              )}
              </>
            )}
          </div>
        </section>

        <section className="stock-management-inventario" aria-label="Vista de inventario">
          <Inventario />
        </section>
      </div>

      {newProductModalOpen &&
        createPortal(
          <div
            className="stock-management-edit-backdrop"
            role="presentation"
            onClick={() => {
              if (!newProductSaving) {
                setNewProductModalOpen(false);
                setNewProductForm({ ...INITIAL_NEW_PRODUCT_FORM });
              }
            }}
          >
            <div
              className="stock-management-edit-modal stock-management-new-product-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="stock-new-product-title"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="stock-management-edit-modal-header">
                <h3 id="stock-new-product-title" className="stock-management-edit-modal-title">
                  Agregar nuevo producto
                </h3>
                <button
                  type="button"
                  className="stock-management-edit-modal-close"
                  onClick={() => {
                    if (!newProductSaving) {
                      setNewProductModalOpen(false);
                      setNewProductForm({ ...INITIAL_NEW_PRODUCT_FORM });
                    }
                  }}
                  aria-label="Cerrar"
                >
                  ×
                </button>
              </header>
              <div className="stock-management-edit-modal-body">
                <p className="stock-management-edit-hint">
                  Se crea el producto en el catálogo con stock 0. Si lo sumás a la carga actual, al confirmar «Agregar stock» se registra el ingreso y el historial como el resto de productos.
                </p>
                <div className="stock-management-edit-field">
                  <label className="stock-management-edit-label" htmlFor="stock-new-product-catalog">
                    Catálogo <abbr title="obligatorio" className="stock-management-required-mark">*</abbr>
                  </label>
                  <select
                    id="stock-new-product-catalog"
                    value={newProductForm.catalogCollection}
                    onChange={(e) => setNewProductForm((f) => ({ ...f, catalogCollection: e.target.value }))}
                    className="stock-management-edit-input"
                    disabled={newProductSaving}
                    required
                  >
                    {STOCK_CATALOGS.map((catalog) => (
                      <option key={catalog.collectionName} value={catalog.collectionName}>
                        {catalog.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="stock-management-edit-field">
                  <label className="stock-management-edit-label" htmlFor="stock-new-product-model">
                    Nombre / modelo <abbr title="obligatorio" className="stock-management-required-mark">*</abbr>
                  </label>
                  <input
                    id="stock-new-product-model"
                    type="text"
                    value={newProductForm.model}
                    onChange={(e) => setNewProductForm((f) => ({ ...f, model: e.target.value }))}
                    className="stock-management-edit-input"
                    disabled={newProductSaving}
                    placeholder="Ej.: Remera básica talle M"
                    maxLength={200}
                  />
                </div>
                <div className="stock-management-edit-field stock-management-new-product-two-col">
                  <div>
                    <label className="stock-management-edit-label" htmlFor="stock-new-product-price">
                      Precio de venta <abbr title="obligatorio" className="stock-management-required-mark">*</abbr>
                    </label>
                    <input
                      id="stock-new-product-price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={newProductForm.price}
                      onChange={(e) => setNewProductForm((f) => ({ ...f, price: e.target.value }))}
                      className="stock-management-edit-input"
                      disabled={newProductSaving}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="stock-management-edit-label" htmlFor="stock-new-product-cost">
                      Precio de costo <abbr title="obligatorio" className="stock-management-required-mark">*</abbr>
                    </label>
                    <input
                      id="stock-new-product-cost"
                      type="number"
                      step="0.01"
                      min="0"
                      value={newProductForm.costPrice}
                      onChange={(e) => setNewProductForm((f) => ({ ...f, costPrice: e.target.value }))}
                      className="stock-management-edit-input"
                      disabled={newProductSaving}
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="stock-management-edit-field">
                  <label className="stock-management-edit-label" htmlFor="stock-new-product-category">
                    Categoría
                  </label>
                  <select
                    id="stock-new-product-category"
                    value={newProductForm.category}
                    onChange={(e) => setNewProductForm((f) => ({ ...f, category: e.target.value }))}
                    className="stock-management-edit-input"
                    disabled={newProductSaving}
                  >
                    <option value="">Sin categoría</option>
                    {productCategories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                    <option value="__custom__">Nueva categoría…</option>
                  </select>
                </div>
                {newProductForm.category === '__custom__' && (
                  <div className="stock-management-edit-field">
                    <label className="stock-management-edit-label" htmlFor="stock-new-product-new-cat">
                      Nombre de la nueva categoría
                    </label>
                    <input
                      id="stock-new-product-new-cat"
                      type="text"
                      value={newProductForm.newCategory}
                      onChange={(e) => setNewProductForm((f) => ({ ...f, newCategory: e.target.value }))}
                      className="stock-management-edit-input"
                      disabled={newProductSaving}
                      placeholder="Ej.: Indumentaria"
                    />
                  </div>
                )}
                <div className="stock-management-edit-field">
                  <label className="stock-management-edit-label" htmlFor="stock-new-product-desc">
                    Descripción (opcional)
                  </label>
                  <textarea
                    id="stock-new-product-desc"
                    rows={3}
                    value={newProductForm.description}
                    onChange={(e) => setNewProductForm((f) => ({ ...f, description: e.target.value }))}
                    className="stock-management-edit-input stock-management-new-product-textarea"
                    disabled={newProductSaving}
                    placeholder="Notas internas o pública según uses en la tienda"
                  />
                </div>
                <div className="stock-management-edit-field">
                  <label className="stock-management-edit-label" htmlFor="stock-new-product-qty">
                    Cantidad del primer ingreso
                  </label>
                  <input
                    id="stock-new-product-qty"
                    type="number"
                    min="1"
                    value={newProductForm.quantity}
                    onChange={(e) => setNewProductForm((f) => ({ ...f, quantity: e.target.value }))}
                    className="stock-management-edit-input"
                    disabled={newProductSaving || !newProductForm.addToSelection}
                    placeholder="1"
                  />
                </div>
                <div className="stock-management-new-product-flags" role="group" aria-label="Opciones de visibilidad y stock">
                  <label className="stock-management-new-product-checkbox">
                    <input
                      type="checkbox"
                      checked={newProductForm.hidden}
                      onChange={(e) => setNewProductForm((f) => ({ ...f, hidden: e.target.checked }))}
                      disabled={newProductSaving}
                    />
                    <span>Producto oculto (no se muestra en el catálogo público)</span>
                  </label>
                  <label className="stock-management-new-product-checkbox">
                    <input
                      type="checkbox"
                      checked={!newProductForm.excludeFromStock}
                      onChange={(e) =>
                        setNewProductForm((f) => ({ ...f, excludeFromStock: !e.target.checked }))
                      }
                      disabled={newProductSaving}
                    />
                    <span>Computa stock (desmarcá para servicios o ítems sin control de stock en la tienda)</span>
                  </label>
                </div>
                <label className="stock-management-new-product-checkbox">
                  <input
                    type="checkbox"
                    checked={newProductForm.addToSelection}
                    onChange={(e) => setNewProductForm((f) => ({ ...f, addToSelection: e.target.checked }))}
                    disabled={newProductSaving}
                  />
                  <span>Añadir a la selección actual (para confirmar con «Agregar stock» junto al resto)</span>
                </label>
              </div>
              <footer className="stock-management-edit-modal-footer">
                <button
                  type="button"
                  className="stock-management-edit-secondary-btn"
                  disabled={newProductSaving}
                  onClick={() => {
                    setNewProductModalOpen(false);
                    setNewProductForm({ ...INITIAL_NEW_PRODUCT_FORM });
                  }}
                >
                  Cancelar
                </button>
                <div className="stock-management-edit-footer-right">
                  <button
                    type="button"
                    className="stock-management-edit-primary-btn"
                    disabled={newProductSaving}
                    onClick={handleCreateNewProduct}
                  >
                    {newProductSaving ? 'Creando…' : 'Crear producto'}
                  </button>
                </div>
              </footer>
              {newProductSaving && (
                <div className="stock-management-edit-spinner-wrap" aria-live="polite">
                  <Spinner />
                </div>
              )}
            </div>
          </div>,
          document.body
        )}

      {editLoadModal &&
        createPortal(
          <div
            className="stock-management-edit-backdrop"
            role="presentation"
            onClick={() => {
              if (!editLoadSaving) setEditLoadModal(null);
            }}
          >
            <div
              className="stock-management-edit-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="stock-edit-load-title"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="stock-management-edit-modal-header">
                <h3 id="stock-edit-load-title" className="stock-management-edit-modal-title">
                  Editar carga
                </h3>
                <button
                  type="button"
                  className="stock-management-edit-modal-close"
                  onClick={() => {
                    if (!editLoadSaving) setEditLoadModal(null);
                  }}
                  aria-label="Cerrar"
                >
                  ×
                </button>
              </header>
              <div className="stock-management-edit-modal-body">
                <p className="stock-management-edit-hint">
                  Si cambiás cantidades o quitás líneas, el stock actual se corrige por la diferencia respecto de lo que figuraba en esta carga. Si ya se vendió parte del ingreso, puede fallar por stock insuficiente.
                </p>

                <div className="stock-management-edit-field">
                  <label className="stock-management-edit-label" htmlFor="stock-edit-op-name">
                    Nombre de la operación
                  </label>
                  <div className="stock-management-edit-name-row">
                    <input
                      id="stock-edit-op-name"
                      type="text"
                      value={editLoadModal.operationName}
                      onChange={(e) =>
                        setEditLoadModal((prev) =>
                          prev ? { ...prev, operationName: e.target.value } : prev
                        )
                      }
                      maxLength={120}
                      className="stock-management-edit-input"
                      disabled={editLoadSaving}
                      placeholder="Opcional"
                    />
                    <button
                      type="button"
                      className="stock-management-edit-secondary-btn"
                      disabled={editLoadSaving}
                      onClick={() =>
                        setEditLoadModal((prev) => (prev ? { ...prev, operationName: '' } : prev))
                      }
                    >
                      Quitar nombre
                    </button>
                  </div>
                </div>

                <div className="stock-management-edit-table-wrap">
                  <table className="stock-management-edit-table">
                    <thead>
                      <tr>
                        <th>Catálogo</th>
                        <th>Producto</th>
                        <th>Unidades ingresadas</th>
                        <th aria-label="Acciones" />
                      </tr>
                    </thead>
                    <tbody>
                      {editLoadModal.lines.map((line, idx) => (
                        <tr key={`${stockLineKey(line)}-${idx}`}>
                          <td>{stockCatalogLabel(stockLineCollection(line))}</td>
                          <td>{line.productModel || '—'}</td>
                          <td>
                            <input
                              type="number"
                              min={1}
                              step={1}
                              className="stock-management-edit-qty-input"
                              value={line.quantityAdded}
                              onChange={(e) =>
                                updateEditLoadLine(idx, 'quantityAdded', e.target.value)
                              }
                              disabled={editLoadSaving}
                              aria-label={`Cantidad para ${line.productModel || line.productId}`}
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="stock-management-edit-remove-line-btn"
                              disabled={editLoadSaving}
                              onClick={() => removeEditLoadLine(idx)}
                            >
                              Quitar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <footer className="stock-management-edit-modal-footer">
                <button
                  type="button"
                  className="stock-management-edit-danger-btn"
                  disabled={editLoadSaving}
                  onClick={deleteWholeLoad}
                >
                  Eliminar operación
                </button>
                <div className="stock-management-edit-footer-right">
                  <button
                    type="button"
                    className="stock-management-edit-secondary-btn"
                    disabled={editLoadSaving}
                    onClick={() => setEditLoadModal(null)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="stock-management-edit-primary-btn"
                    disabled={editLoadSaving}
                    onClick={saveEditLoad}
                  >
                    {editLoadSaving ? 'Guardando…' : 'Guardar cambios'}
                  </button>
                </div>
              </footer>
              {editLoadSaving ? (
                <div className="stock-management-edit-spinner-wrap">
                  <Spinner />
                </div>
              ) : null}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default StockManagement;
